import React, {Dispatch, SetStateAction, useCallback} from 'react';
import isEqual from 'react-fast-compare';

import {MaterialFlow, ProcedureFlowchart} from '../ProcedureDomainTypes';
import {normalizeFlowchartOrderingForComparison} from '../procedurePagePersistenceUtils';
import {getMaterialFlows} from './flowchartManagementUtils';

export interface UseSetLegacyLocalDraftFlowchartParams {
  legacyDraftFlowchartRef: React.MutableRefObject<ProcedureFlowchart>;
  legacyPersistedFlowchart: ProcedureFlowchart;
  setLegacyDraftFlowchart: (fc: ProcedureFlowchart) => void;
  setLegacyMaterialFlows: Dispatch<SetStateAction<Array<MaterialFlow>>>;
  legacySetProcedureDirty: (isDirty: boolean) => void;
}

/**
 * Returns the callback for applying an edit to the legacy draft flowchart. The updater is resolved against
 * `legacyDraftFlowchartRef.current` (the synchronously-current draft), so successive edits in the same tick
 * build on each other, and the result is handed to `setLegacyDraftFlowchart` synchronously — capturing the
 * edit and queuing its Redux dispatch the instant it happens. See the draft-state overview on
 * `PresentationalProcedurePage`.
 */
export function useSetLegacyLocalDraftFlowchart({
  legacyDraftFlowchartRef,
  legacyPersistedFlowchart,
  setLegacyDraftFlowchart,
  setLegacyMaterialFlows,
  legacySetProcedureDirty,
}: UseSetLegacyLocalDraftFlowchartParams): (
  fcUpdater: (fc: ProcedureFlowchart) => ProcedureFlowchart
) => void {
  return useCallback(
    (fcUpdater: (fc: ProcedureFlowchart) => ProcedureFlowchart): void => {
      const updatedFc = fcUpdater(legacyDraftFlowchartRef.current);
      if (
        !isEqual(
          normalizeFlowchartOrderingForComparison(updatedFc),
          normalizeFlowchartOrderingForComparison(legacyPersistedFlowchart)
        )
      ) {
        legacySetProcedureDirty(true);
      }
      setLegacyMaterialFlows(getMaterialFlows(updatedFc));
      setLegacyDraftFlowchart(updatedFc);
    },
    [
      legacyDraftFlowchartRef,
      legacyPersistedFlowchart,
      setLegacyMaterialFlows,
      legacySetProcedureDirty,
      setLegacyDraftFlowchart,
    ]
  );
}
